/**
 * API functions for user metadata operations.
 * Uses legacy FHMetaData/FHMetaDataRequests models via datastore keys.
 */
import { apiGet, apiPost } from './utils';

export interface MetadataType {
  key: string;
  title: string;
  description: string;
  client_description: string;
  type: 'string' | 'text' | 'integer' | 'float' | 'boolean' | 'datetime' | 'date';
  reference: string;
  accepted: boolean;
  rejected: boolean;
  show_to_client: boolean;
  date_created: string;
}

export interface UserMetadata {
  key: string;
  metadata_request: string;
  metadata_type: string; // title
  metadata_type_type: string; // data type
  ref: string;
  value: string | number | boolean | null;
  created_by: string;
  created: string;
  removed: boolean;
  edited: boolean;
}

export interface ListResponse<T> {
  results: T[];
}

export const userMetadataApi = {
  /**
   * Get all accepted metadata types.
   */
  async getMetadataTypes(): Promise<ListResponse<MetadataType>> {
    return apiGet<ListResponse<MetadataType>>('/metadata-type/list');
  },

  /**
   * Get a single metadata type by key.
   */
  async getMetadataType(key: string): Promise<MetadataType> {
    return apiGet<MetadataType>('/metadata-type/', { key });
  },

  /**
   * Create a new user metadata entry.
   */
  async createUserMetadata(params: {
    ref: string; // user key
    metadata_request: string; // metadata type key
    value: string | number | boolean;
  }): Promise<UserMetadata> {
    return apiPost<UserMetadata>('/user-metadata/create', params);
  },

  /**
   * Update an existing user metadata entry.
   */
  async updateUserMetadata(params: {
    key: string;
    value: string | number | boolean;
  }): Promise<UserMetadata> {
    return apiPost<UserMetadata>('/user-metadata/update', params);
  },

  /**
   * Remove (soft-delete) a user metadata entry.
   */
  async removeUserMetadata(key: string): Promise<UserMetadata> {
    return apiPost<UserMetadata>('/user-metadata/remove', { key });
  },

  /**
   * Get a single user metadata entry by key.
   */
  async getUserMetadata(key: string): Promise<UserMetadata> {
    return apiGet<UserMetadata>('/user-metadata/', { key });
  },

  /**
   * List all metadata for a user (by ref key).
   */
  async listByRef(refKey: string): Promise<ListResponse<UserMetadata>> {
    return apiGet<ListResponse<UserMetadata>>('/user-metadata/list/by-ref', {
      ref: refKey,
    });
  },
};
